import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useColorScheme,
  useMediaQuery,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';

import { Close } from '@mui/icons-material';
import { primary } from '../../../../../utils/brandColors';
import {
  getRoleDescription,
  getRoleFeatures,
  getRoleList,
} from '../../../../../utils/rolesHelper';
import theme from '../../../../../utils/theme';
import styles from './styles';

const useStyles = makeStyles(styles);
const capitalized = (word) => (
  word.charAt(0).toUpperCase()
  + word.slice(1));

const userRoleCard = {
  key: 'user',
  title: 'User (Client)',
  description: (
    <Typography variant="body2" color="text.secondary">
      Basic profile access for clients and kiosk displays. Users can view or edit their profile,
      update notification preferences, view the Digital Barn Board, and engage with tasks they are
      assigned to. They need explicit horse access to see Client Experience. Ideal for barn clients
      and unattended TV/kiosk displays in the barn. This role provides secure, read-only access to
      the barn board, preventing unauthorized access to sensitive information.
    </Typography>
  ),
  features: [
    'View/edit profile information',
    'View Digital Barn Board',
    'Update notification preferences',
    'Track assigned lessons, rides, or billing tasks',
  ],
};
export const AboutUserRolesModalComponent = (props) => {
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { mode } = useColorScheme();
  const {
    open,
    onClose,
  } = props;
  const classes = useStyles();
  const specialtyRoles = ['farrier', 'veterinarian'];
  const roleCards = [
    userRoleCard,
    ...getRoleList().filter((role) => specialtyRoles.indexOf(role) < 0).map((role) => ({
      key: role,
      title: capitalized(role),
      description: getRoleDescription(role),
      features: getRoleFeatures(role),
    })),
  ];
  const specialtyCards = specialtyRoles.map((role) => ({
    key: role,
    title: capitalized(role),
    description: getRoleDescription(role),
    features: getRoleFeatures(role),
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      aria-labelledby="user-role-info"
      aria-describedby="user-role-info"
      id="user-role-info-dialog"
      className={classes.root}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle
        id="remove-user-title"
        sx={{
          background: mode === 'light'
            ? primary.main
            : 'var(--template-palette-background-paper)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        Questri Roles
        <IconButton onClick={onClose}><Close sx={{ color: '#fff' }} /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography color="inherit" className={classes.userDescription}>
          When you add a user to the Questri organization, they are assigned
          a <b>User</b> role by default. This role is meant for <b>clients</b> and
          only allows them access to view/edit their profile.
          In addition, these users can also be assigned tasks and
          provide updates on those tasks (Lessons, Rides, Billing, etc.).
        </Typography>
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ my: 2 }}><b>Everyday Roles</b></Typography>
          <Grid container spacing={2}>
            {roleCards.map((role) => (
              <Grid item xs={12} sm={6} key={role.key}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Chip
                      label={role.title}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ '& p': { mb: 1 } }}>
                    {role.description}
                  </Box>
                  <List dense disablePadding>
                    {role.features.map((feature) => (
                      <ListItem
                        key={`${role.key}-${feature}`}
                        sx={{ pl: 0 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ mt: 2 }}><b>Specialty Roles</b></Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Great for occasional collaborators like farriers or veterinarians.</Typography>
          <Grid container spacing={2}>
            {specialtyCards.map((role) => (
              <Grid item xs={12} sm={6} key={role.key}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Chip
                      label={role.title}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ '& p': { mb: 1 } }}>
                    {role.description}
                  </Box>
                  <List dense disablePadding>
                    {role.features.map((feature) => (
                      <ListItem
                        key={`${role.key}-${feature}`}
                        sx={{ pl: 0 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          id="close-role-info-dialog"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AboutUserRolesModalComponent.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
AboutUserRolesModalComponent.defaultProps = {
  open: false,
  onClose: () => { },
};

export default AboutUserRolesModalComponent;
